import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Award } from "lucide-react";
import { seasonService } from "@/services";

// Inline countdown chip: "Season ends in 12d 4h".
// Sister to DailyCountdown but pegged to the season's `expires_at`
// (first day of next month UTC, supplied by `/api/season-pass`).
// Hides quietly if the season fetch fails — the chip is decorative.
function formatRemaining(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// `variant` controls colors so the chip stays readable wherever it sits:
//   "onHero" — white-on-translucent, for use on the colored season hero card.
//   "neutral" — purple-on-light, for plain backgrounds.
export default function SeasonCountdown({ testId = "season-countdown", variant = "onHero" }) {
  const { t } = useTranslation();
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    let cancelled = false;
    seasonService
      .getCurrentSeason()
      .then((s) => {
        if (cancelled) return;
        setExpiresAt(s?.expires_at || null);
      })
      .catch(() => {/* swallow */});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const target = Date.parse(expiresAt);
    if (!Number.isFinite(target)) return undefined;
    const update = () => setRemaining(target - Date.now());
    update();
    const tick = window.setInterval(update, 1000);
    return () => window.clearInterval(tick);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const classes =
    variant === "onHero"
      ? "border border-white/30 bg-white/15 text-white"
      : "border border-[#8247E5]/30 bg-[#8247E5]/10 text-[#8247E5]";

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-bold ${classes}`}
    >
      <Award className="w-3 h-3" />
      {t("seasonCountdown.endsIn", { remaining: formatRemaining(remaining) })}
    </span>
  );
}
